import { useEffect, useRef } from 'react';

/**
 * The scroll container every profile tab pages inside. Owned by `Profile.js`
 * (line 1218), which is why this hook looks it up by id rather than taking a
 * ref — the tabs are rendered as children and never hold one.
 */
const SCROLL_CONTAINER_ID = 'profile-content';

/**
 * Distance from the bottom, in px, at which "near the bottom" begins.
 *
 * Carried over verbatim from the three copies this replaces, comment included:
 * it is the navbar's height in its expanded state, and it should really be
 * measured off the `.navbar` element rather than hardcoded.
 */
const TOLERANCE = 102;

/**
 * Calls `onReachBottom` when the profile's content column is scrolled near its
 * bottom — the infinite-scroll trigger for the Reviews, Receipts and Invoices
 * tabs.
 *
 * Extracted because the same effect was copy-pasted three times
 * (`UserReview.js`, `Receipts/ReceiptList.js`, `invoices.js`), each with the
 * same magic `102` and the same two `eslint-disable`s. Only the "can I load
 * more?" predicate and the load action differ, and those belong to whoever owns
 * the data — hence `enabled` and the callback.
 *
 * Both arguments are held in refs so the effect's dependency array is empty:
 * the listener attaches once on mount and detaches on unmount, while still
 * seeing the latest values. The originals re-attached on every data change and
 * referenced their handler before its `useCallback` had run — which worked only
 * because effects run after render. Keeping them in refs is behaviour-identical
 * and removes both disables.
 *
 * @param {() => void} onReachBottom - Called once per qualifying scroll event
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - When false the callback never
 *   fires. Pass the caller's own guard here — for React Query that must include
 *   `!isError`, or a failed page refires on every subsequent scroll event.
 */
export const useProfileContentScroll = (onReachBottom, { enabled = true } = {}) => {
  const onReachBottomRef = useRef(onReachBottom);
  const enabledRef = useRef(enabled);

  onReachBottomRef.current = onReachBottom;
  enabledRef.current = enabled;

  useEffect(() => {
    const container = document.getElementById(SCROLL_CONTAINER_ID);

    // Absent when a tab is rendered outside Profile.js — in a test, or if the
    // shell changes. Nothing to page against, so do nothing.
    if (container == null) return undefined;

    const handleScroll = (event) => {
      if (!enabledRef.current) return;

      const sectionHeight = event.target.getBoundingClientRect().height;
      const elementMaxScrollExtent = event.target.scrollHeight - TOLERANCE;
      const scrollProgress = event.target.scrollTop + sectionHeight;

      if (scrollProgress >= elementMaxScrollExtent) {
        onReachBottomRef.current();
      }
    };

    container.addEventListener('scroll', handleScroll);

    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
};

export default useProfileContentScroll;
